import { useActiveVkuiLocation } from '@vkontakte/vk-mini-apps-router';
import { FormItem, Input, Textarea, type NavIdProps } from '@vkontakte/vkui';
import { useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../api/client';
import { usePagedLoader } from '../../api/paging';
import { useSession } from '../../api/session';
import { ROLE_LABELS, type Page, type UserRole } from '../../api/types';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { PlusIcon } from '../../shared/icons';
import { useCardScroll } from '../../shared/lib/cardScroll';
import { useSearchTerm } from '../../shared/lib/search';
import {
  Action,
  ActionRow,
  CardHeading,
  CardLink,
  CardList,
  EmptyState,
  ErrorState,
  FieldLabel,
  FilterBar,
  FilterSelect,
  Gap,
  Gutter,
  LoadingState,
  Meta,
  Muted,
  NoAccess,
  Screen,
  SearchField,
  Section,
  SectionTitle,
  Select,
  StatusBadge,
  TabRow,
  type FilterOption,
  type TabRowOption,
} from '../../shared/ui';
import { AdminConfigSection } from '../app-config/AdminConfig';
import { AuditLogSection } from '../audit/AuditLog';
import { AdminCharactersSection, type AdminCharacter } from '../characters/AdminCharacters';
import {
  Notice,
  NoticeBlock,
  ShowMore,
  canAct,
  canReviewForms,
  isAdmin,
  isStaffSession,
  personName,
} from '../review/staffKit';
import { AdminUserCard } from './AdminUserCard';

/**
 * Админка целиком: список участников, персонажи, справочники и журнал.
 *
 * Всё живёт одной панелью, потому что в router.ts под админку заведён ровно
 * один идентификатор (MORE_ADMIN), а роутер — не наша зона. Разделы
 * переключаются состоянием: в историю переходы между ними не пишутся, и
 * кнопка «назад» в ВК уводит из админки, а не отматывает клики по разделам.
 *
 * Файл называется AdminUsers, потому что вайтлист — главный экран админки:
 * без строки в нём человек в проекте вообще ничего не может.
 */

export interface AdminUser {
  id: number;
  vkUserId: number;
  role: UserRole;
  /** false = исключён из вайтлиста: остаётся только чтение. */
  canWrite: boolean;
  revokedAt: string | null;
  deletedAt: string | null;
  hideVkProfile: boolean;
  displayName: string | null;
  note: string | null;
  createdAt: string;
}

type AdminSection = 'users' | 'characters' | 'config' | 'audit';

const ROLE_OPTIONS = (Object.keys(ROLE_LABELS) as UserRole[]).map((value) => ({
  value,
  label: ROLE_LABELS[value],
}));

const ROLE_FILTERS: readonly FilterOption<'all' | UserRole>[] = [
  { value: 'all', label: t('user.admin.roleFilterAll') },
  { value: 'player', label: ROLE_LABELS.player },
  { value: 'form_moderator', label: ROLE_LABELS.form_moderator },
  { value: 'quest_master', label: ROLE_LABELS.quest_master },
  { value: 'superadmin', label: ROLE_LABELS.superadmin },
];

export function AdminPanel({ id }: NavIdProps) {
  const session = useSession();
  const goBack = useBack();
  const { panel } = useActiveVkuiLocation();
  const active = id !== undefined && panel === id;

  const [section, setSection] = useState<AdminSection | null>(null);
  const [openUser, setOpenUser] = useState<AdminUser | null>(null);
  // Выбранный персонаж живёт здесь, а не в своей секции: возврат в шапке
  // обязан закрывать его карточку — своей кнопки у неё больше нет.
  const [openCharacter, setOpenCharacter] = useState<AdminCharacter | null>(null);

  const sections: TabRowOption<AdminSection>[] = [];
  if (isAdmin(session)) sections.push({ value: 'users', label: t('common.section.members') });
  if (canReviewForms(session))
    sections.push({ value: 'characters', label: t('common.section.characters') });
  if (isAdmin(session)) sections.push({ value: 'config', label: t('user.admin.section.config') });
  if (isStaffSession(session))
    sections.push({ value: 'audit', label: t('user.admin.section.audit') });

  const back = () => {
    if (openUser) setOpenUser(null);
    else if (openCharacter !== null) setOpenCharacter(null);
    else goBack();
  };

  if (sections.length === 0) {
    return (
      <Screen id={id} title={t('user.admin.title')} back={back}>
        <NoAccess>{t('user.admin.noAccess')}</NoAccess>
      </Screen>
    );
  }

  const current =
    section && sections.some((row) => row.value === section) ? section : sections[0].value;

  // Ряд разделов, поиск и фильтры — одна секция навигации над списком, а поиск
  // и фильтры держит подраздел: ряд отдаётся ему, и он ставит его первым в
  // свою полосу. Пока ряд стоял своей секцией, от поиска его отбивал шов
  // между секциями — 20 вместо шага полосы 10, как в реестре.
  const tabs = openUser ? null : (
    <TabRow options={sections} value={current} onChange={setSection} />
  );

  return (
    <Screen
      id={id}
      title={openUser ? t('user.admin.userTitle') : t('user.admin.title')}
      back={back}
    >
      {current === 'users' ? (
        <WhitelistSection
          tabs={tabs}
          active={active}
          user={openUser}
          onOpen={setOpenUser}
          onChanged={setOpenUser}
        />
      ) : null}
      {current === 'characters' ? (
        <AdminCharactersSection
          tabs={tabs}
          active={active}
          character={openCharacter}
          onOpen={setOpenCharacter}
          onChanged={setOpenCharacter}
          onGone={() => setOpenCharacter(null)}
        />
      ) : null}
      {current === 'config' ? (
        <>
          <Section>{tabs}</Section>
          <AdminConfigSection active={active} />
        </>
      ) : null}
      {current === 'audit' ? <AuditLogSection tabs={tabs} active={active} /> : null}
    </Screen>
  );
}

/* ── Вайтлист ────────────────────────────────────────────────────────────── */

function WhitelistSection({
  tabs,
  active,
  user,
  onOpen,
  onChanged,
}: {
  tabs: ReactNode;
  active: boolean;
  user: AdminUser | null;
  onOpen: (user: AdminUser) => void;
  onChanged: (user: AdminUser) => void;
}) {
  const session = useSession();
  // Поиск с задержкой — тот же, что у имущества: список постраничный, и ходить
  // на сервер за каждой буквой незачем.
  const search = useSearchTerm();
  const [role, setRole] = useState<'all' | UserRole>('all');
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState<string | null>(null);

  const writable = canAct(session);

  const users = usePagedLoader<AdminUser>(
    (params, signal) =>
      api.get<Page<AdminUser>>(
        '/api/users',
        {
          search: search.term || undefined,
          role: role === 'all' ? undefined : role,
          limit: params.limit,
          offset: params.offset,
        },
        signal,
      ),
    [search.term, role],
    active,
  );

  const rememberScroll = useCardScroll(user !== null);

  // Карточка — здесь, после хуков: смонтированной остаётся секция, и её хуки
  // держат поиск, роль и дочитанные страницы. Сам список, пока открыта карточка, не рисуется.
  if (user) {
    return (
      <AdminUserCard
        user={user}
        onChanged={(next) => {
          onChanged(next);
          users.reload();
        }}
      />
    );
  }

  return (
    <>
      <Section>
        {tabs}

        {/* Поиск и заведение — одна полоса: обе кнопки относятся к списку ниже. */}
        <SearchField
          value={search.input}
          onChange={search.setInput}
          placeholder={t('user.whitelist.searchPlaceholder')}
          action={
            <Action
              tone="action"
              size="m"
              before={<PlusIcon />}
              disabled={!writable || adding}
              onClick={() => setAdding(true)}
            >
              {t('common.action.add')}
            </Action>
          }
        />

        {writable ? null : (
          <Gutter>
            <Muted>{t('user.whitelist.readOnlyHint')}</Muted>
          </Gutter>
        )}

        <FilterBar>
          <FilterSelect
            label={t('user.field.role')}
            options={ROLE_FILTERS}
            value={role}
            onChange={setRole}
          />
        </FilterBar>
      </Section>

      {adding ? (
        <AddUserForm
          onAdded={(message) => {
            setAdded(message);
            users.reload();
          }}
          onClose={() => setAdding(false)}
        />
      ) : null}

      {added ? <NoticeBlock tone="success">{added}</NoticeBlock> : null}

      <Section>
        <SectionTitle>
          {users.data
            ? t('user.whitelist.titleCount', { total: users.data.total })
            : t('common.section.members')}
        </SectionTitle>

        {users.loading && !users.data ? <LoadingState /> : null}
        {users.error ? <ErrorState message={users.error} onRetry={users.reload} /> : null}

        {users.data && users.data.items.length === 0 ? (
          <EmptyState title={t('user.whitelist.empty.title')}>
            {t('user.whitelist.empty.text')}
          </EmptyState>
        ) : null}

        <CardList>
          {users.data?.items.map((row) => (
            <UserRow
              key={row.id}
              user={row}
              onOpen={() => {
                rememberScroll();
                onOpen(row);
              }}
            />
          ))}
        </CardList>

        <ShowMore page={users.data} onMore={users.loadMore} loading={users.loading} />
      </Section>
    </>
  );
}

/**
 * Строка вайтлиста. Нажимается целиком: раньше это была SimpleCell, где id и
 * роль были склеены в один subtitle — тот не переносит строки, и на 375 px
 * роль съедало многоточие.
 */
function UserRow({ user, onOpen }: { user: AdminUser; onOpen: () => void }) {
  const name = personName(user);
  // Безымянная запись уже подписана как «id 123» — второй раз тот же id не нужен.
  const named = Boolean(user.displayName?.trim());

  return (
    <CardLink
      label={t('user.whitelist.openRow', { name })}
      tone={user.canWrite ? 'default' : 'rejected'}
      chevron
      onClick={onOpen}
    >
      <CardHeading
        badge={
          <>
            {user.canWrite ? null : (
              <StatusBadge tone="rejected">{t('user.state.revoked')}</StatusBadge>
            )}
            {user.deletedAt === null ? null : (
              <StatusBadge tone="archived">{t('user.state.deleted')}</StatusBadge>
            )}
          </>
        }
      >
        {name}
      </CardHeading>

      <Gap step="s1h" />
      <Meta>
        {[named ? t('user.value.vkId', { id: user.vkUserId }) : null, ROLE_LABELS[user.role]]}
      </Meta>
    </CardLink>
  );
}

/* ── Добавление в вайтлист ───────────────────────────────────────────────── */

function AddUserForm({
  onAdded,
  onClose,
}: {
  onAdded: (message: string) => void;
  onClose: () => void;
}) {
  const [vkUserId, setVkUserId] = useState('');
  const [role, setRole] = useState<UserRole>('player');
  const [displayName, setDisplayName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const numeric = /^\d+$/.test(vkUserId.trim());

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const added = vkUserId.trim();
      await api.post('/api/users', {
        vkUserId: Number(added),
        role,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setVkUserId('');
      setDisplayName('');
      setNote('');
      setRole('player');
      onAdded(t('user.add.added', { id: added }));
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section>
      <SectionTitle>{t('user.add.title')}</SectionTitle>

      <FormItem
        top={<FieldLabel hint={t('user.add.hint.vkId')}>{t('user.add.field.vkId')}</FieldLabel>}
        status={vkUserId && !numeric ? 'error' : 'default'}
        bottom={vkUserId && !numeric ? t('user.add.numericOnly') : undefined}
      >
        <Input
          value={vkUserId}
          inputMode="numeric"
          placeholder={t('user.add.placeholder.vkId')}
          disabled={busy}
          onChange={(event) => setVkUserId(event.target.value.replace(/\D/g, ''))}
        />
      </FormItem>

      <FormItem
        top={<FieldLabel hint={t('user.add.hint.role')}>{t('user.field.role')}</FieldLabel>}
      >
        <Select
          value={role}
          disabled={busy}
          options={ROLE_OPTIONS}
          onChange={(event) => setRole(event.target.value as UserRole)}
        />
      </FormItem>

      <FormItem
        top={
          <FieldLabel hint={t('user.add.hint.displayName')}>
            {t('user.add.field.displayName')}
          </FieldLabel>
        }
      >
        <Input
          value={displayName}
          disabled={busy}
          placeholder={t('user.add.placeholder.displayName')}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </FormItem>

      <FormItem
        top={<FieldLabel hint={t('user.add.hint.note')}>{t('common.field.note')}</FieldLabel>}
      >
        <Textarea
          value={note}
          rows={3}
          disabled={busy}
          onChange={(event) => setNote(event.target.value)}
        />
      </FormItem>

      {error ? (
        <Gutter>
          <Notice tone="error" title={t('user.add.error')}>
            {error}
          </Notice>
        </Gutter>
      ) : null}

      <Gutter>
        <ActionRow>
          <Action
            tone="action"
            size="m"
            stretched
            loading={busy}
            disabled={busy || !numeric}
            onClick={() => void submit()}
          >
            {t('common.action.add')}
          </Action>
          <Action tone="quiet" size="m" stretched disabled={busy} onClick={onClose}>
            {t('common.action.collapse')}
          </Action>
        </ActionRow>
      </Gutter>
    </Section>
  );
}
